import { GetInitialDataCtx, WidgetProps } from "../../contracts/widget"
import { propertyTitleWidgetConfig, PropertyTitleWidgetConfig } from "./config"
import { WidgetProvider, useWidget } from "../../providers/widget"
import { useFetchPropertyTitle } from "./data"
import "./property-title.css"
import { useMemo } from "react"
import Stylesheet from "../../components/Stylesheet"
import WebFont from "../../components/WebFont"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { parseWidgetConfig } from "@homerunner-next/widget-core/schema"
import { hostFontSizeCss } from "../../common/host-scope"

export const getInitialData = async ({ options, feedId, widgetId }: GetInitialDataCtx<PropertyTitleWidgetConfig>) => {
    return {}
}

/**
 * Dark-palette override for the auto FIRST-PAINT path (#900). The base
 * cssString always emits the resolved-theme palette (so light output stays
 * byte-identical); this appends the DARK title color under two selector
 * prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto), reached from inside the shadow stylesheet.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 * `color` (from darkModeColors) is the only theme-gated declaration here, so
 * it is all that inverts. Computed from `options.darkModeColors` directly (not
 * gated on resolvedTheme) so these rules ship on every render. `:host(...)`
 * (not a descendant `[data-hr-theme]`) is required to reach the shadow host
 * from inside its own stylesheet, and BOTH auto conditions must sit INSIDE
 * `:host(...)` — `:host([data-hr-scheme="auto"]):not([data-hr-theme])` would
 * silently never match the host.
 */
function propertyTitleDarkOverrides(prefix: string, dc: PropertyTitleWidgetConfig['darkModeColors']): string {
    return `
        ${prefix} .hr-property-title-container *,
        ${prefix} .hr-property-title-container {
            color: ${dc.textColor} !important;
        }
    `
}

function PropertyTitleContent() {
    const { options, resolvedTheme, widgetId } = useWidget<PropertyTitleWidgetConfig>()
    const { data: property, isLoading } = useFetchPropertyTitle(options.filter)

    const Tag = options.tag ?? 'h1'
    const colors = resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors

    const style: React.CSSProperties = {
        textAlign: options.textAlign ?? 'left',
        color: colors.textColor,
        fontFamily: options.font.family,
        ...resolveWidgetSpacingStyle(options),
    }

    const cssString = `
        .hr-property-title-container *, .hr-property-title-container {
            font-family: '${options.font.family}' !important;
            color: ${colors.textColor} !important;
        }
        ${hostFontSizeCss(widgetId, options.font.size)}
        ${propertyTitleDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors)}
        @media (prefers-color-scheme: dark) {
            ${propertyTitleDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors)}
        }
        ${options.customCss ?? ''}
    `

    if (isLoading) {
        return (
            <div className="hr-property-title-container" style={style}>
                <WebFont />
                <Stylesheet css={cssString} />
                <Tag className="hr-property-title" style={{ ...style, opacity: 0.3 }}>Loading...</Tag>
            </div>
        )
    }

    const showLocation = options.showLocation === true && !!property?.city?.name
    const showType = options.showType === true && !!property?.type?.name
    const showRating = options.showRating === true && !!property?.rating && property.rating.count > 0

    return (
        <div className="hr-property-title-container" style={style}>
            <WebFont />
            <Stylesheet css={cssString} />
            <Tag className="hr-property-title" style={style}>
                {property?.displayName ?? ''}
            </Tag>
            {(showLocation || showType || showRating) && (
                <div
                    className="hr-property-title-meta"
                    style={{
                        justifyContent: options.textAlign === 'center' ? 'center'
                            : options.textAlign === 'right' ? 'flex-end' : 'flex-start',
                    }}
                >
                    {showLocation && (
                        <span className="hr-property-title-meta-item hr-property-title-location">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                            {property!.city!.name}
                        </span>
                    )}
                    {showType && (
                        <span className="hr-property-title-meta-item hr-property-title-type">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
                            {property!.type!.name}
                        </span>
                    )}
                    {showRating && (
                        <span className="hr-property-title-meta-item hr-property-title-rating" title={`${property!.rating!.count} reviews`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="#f5a623" stroke="none"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                            {property!.rating!.average.toFixed(1)} ({property!.rating!.count})
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}

export default function PropertyTitleWidget({ options, feedId, widgetId, resolvedTheme, data }: WidgetProps<PropertyTitleWidgetConfig>) {
    const parsedOptions = useMemo(() => parseWidgetConfig(propertyTitleWidgetConfig, options), [options])
    return (
        <WidgetProvider options={parsedOptions} feedId={feedId} widgetId={widgetId} resolvedTheme={resolvedTheme} widgetType="property-title" data={data ?? {}}>
            <PropertyTitleContent />
        </WidgetProvider>
    )
}
